import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { POPUP_CLASSNAME } from "constants/common.constants";
import { useScrollThumb } from "hooks/useScrollThumb";
import { CloseIcon } from "./docs.icons";

type ShipmentDrawerProps = {
  /** The design's `data-kind`: what the drawer holds — `container` documents, `notes`, … */
  kind: string;
  title: string;
  sub?: string;
  /** Beside the title, before the close button — the design's Upload. */
  action?: ReactNode;
  onClose: () => void;
  /** The scrolling body, drawn inside `.shdoc__list`. */
  children: ReactNode;
};

/**
 * The design's `#shdoc`: a panel that slides over the page from the right.
 * Documents fill it here; the same frame takes a container's note.
 *
 * It renders into the node next to the app root, as the modals do, so no
 * stacking context up the host page can trap it. The body class is the
 * design's scroll lock; the list scrolls with the custom thumb.
 */
export const ShipmentDrawer = ({ kind, title, sub, action, onClose, children }: ShipmentDrawerProps) => {
  const [host, setHost] = useState<HTMLElement | null>(null);
  const { listRef, thumbRef } = useScrollThumb(host);

  useEffect(() => {
    setHost(document.querySelector<HTMLElement>(`.${POPUP_CLASSNAME}`));
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // A confirmation open over the drawer takes Esc first, as in the design.
      if (event.key !== "Escape" || document.querySelector(".pcmodal") !== null) return;

      onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    document.body.classList.add("is-shdoc");

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.classList.remove("is-shdoc");
    };
  }, [onClose]);

  if (host === null) return null;

  return createPortal(
    <aside className="shdoc" data-kind={kind} aria-label={title}>
      <div className="shdoc__back" onClick={onClose} />
      <div className="shdoc__panel" role="dialog" aria-modal="true" aria-label={title}>
        <div className="shdoc__top">
          <div className="shdoc__ttl">
            <div className="shdoc__h">{title}</div>
            {sub && <p className="shdoc__sub">{sub}</p>}
          </div>
          {action}
          <button type="button" className="shdoc__x" aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <div className="shdoc__wrap">
          <div className="shdoc__list" ref={listRef}>
            {children}
          </div>
          <div className="shdoc__thumb" ref={thumbRef} />
        </div>
      </div>
    </aside>,
    host
  );
};
